import type { AxiosError } from 'axios';

/**
 * Normalised error shape thrown by every failed API call.
 *
 * The .NET API returns validation errors wrapped in ASP.NET's ProblemDetails
 * format (`title`, `errors` map) and business errors as `{ message: string }`.
 * `fromAxiosError` handles both; everything else is coerced to a generic 500.
 */
export interface ApiError {
  /** HTTP status code, or 0 for network-level failures. */
  status: number;
  /** Machine-readable error code when the API provides one. */
  code?: string;
  /** Human-readable description suitable for display. */
  message: string;
  /** Raw error payload for debugging or field-level validation. */
  details?: unknown;
}

/**
 * Type-only predicate for AxiosError so we can discriminate without importing
 * the axios runtime here. The `isAxiosError` runtime guard lives in client.ts
 * (the only place that imports axios directly); we use the duck-typed shape
 * here to keep this module free of the axios import.
 */
function isAxiosLike(err: unknown): err is AxiosError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'isAxiosError' in err &&
    (err as { isAxiosError: unknown }).isAxiosError === true
  );
}

/**
 * True when the value is already a normalised {@link ApiError}.
 *
 * Matters because `fromAxiosError` runs **twice** on every failure: once in
 * `client.ts`'s response interceptor, which rejects with the resulting
 * `ApiError`, and again at the call site (`.catch(err => fromAxiosError(err))`
 * — the pattern used by every list and detail page). An `ApiError` is a plain
 * object, so it is neither Axios-like nor an `Error` instance; without this
 * guard the second pass fell through to the unknown-value branch and replaced
 * the real message with "An unexpected error occurred." while zeroing `status`.
 *
 * That is how a permission 401 (`{"message":"Unauthorized"}`) reached the user
 * as a generic failure, and why no caller could branch on the status code.
 */
function isApiError(err: unknown): err is ApiError {
  return (
    typeof err === 'object' &&
    err !== null &&
    typeof (err as ApiError).status === 'number' &&
    typeof (err as ApiError).message === 'string' &&
    !('isAxiosError' in err)
  );
}

/**
 * Builds an `ApiError` from an unknown thrown value.
 *
 * Idempotent: passing an `ApiError` back in returns it unchanged, so the
 * interceptor and the call site can both normalise without the second call
 * discarding what the first extracted.
 *
 * Uses the duck-typed Axios error predicate to discriminate before touching
 * `.response` so we never accidentally access properties on a plain Error or
 * non-object.
 */
export function fromAxiosError(err: unknown): ApiError {
  if (isApiError(err)) return err;

  if (isAxiosLike(err)) {
    const status = err.response?.status ?? 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- response data is untyped by design here; we extract what we need safely
    const body = err.response?.data as Record<string, any> | undefined;

    // ASP.NET ProblemDetails validation envelope
    if (body?.title === 'One or more validation errors occurred.' && body?.errors) {
      const firstField = Object.keys(body.errors)[0];
      const firstMsg: string =
        Array.isArray(body.errors[firstField]) ? body.errors[firstField][0] : String(body.errors[firstField]);
      return {
        status,
        code: 'VALIDATION_ERROR',
        message: firstMsg,
        details: body.errors,
      };
    }

    return {
      status,
      code: body?.code,
      message: body?.message ?? err.message ?? 'An unexpected error occurred.',
      details: body,
    };
  }

  // Non-Axios error (e.g. thrown manually in tests or interceptors)
  if (err instanceof Error) {
    return { status: 0, message: err.message };
  }

  return { status: 0, message: 'An unexpected error occurred.' };
}

/**
 * Paginated list response returned by the .NET API for list endpoints.
 *
 * Mirrors `COMMON.Pagination<T>` in the .NET backend
 * (`Backend/LILNS_ITOW_DES/COMMON/Response.cs`). The C# property names are
 * lowercase and serialize verbatim, so these field names match the wire shape
 * exactly.
 *
 * Note: in the .NET source the generic argument is the element collection type
 * (e.g. `Pagination<List<T>>` or `Pagination<TEntity[]>`), so `elements` here
 * is `T[]` rather than `T`.
 */
export interface PaginatedResponse<T> {
  elements: T[];
  page: number;
  from: number;
  totalPage: number;
  pageSize: number;
  totalRecords: number;
}

/**
 * Pagination-only projection of `PaginatedResponse<T>` — drops the `elements`
 * array and keeps the 5 metadata fields list pages need to render their
 * pagination footer (totalRecords, totalPage, page, from, pageSize). Lives
 * here so the 14 list pages stop redeclaring this `Pick<>` per file.
 */
export type PaginationMeta<T> = Pick<
  PaginatedResponse<T>,
  'totalRecords' | 'totalPage' | 'page' | 'from' | 'pageSize'
>;

/**
 * Project a fresh `PaginatedResponse<T>` down to the `PaginationMeta<T>`
 * shape used by list page state. The verbatim 6-line `setMeta({ totalRecords:
 * result.totalRecords, totalPage: result.totalPage, page: result.page, from:
 * result.from, pageSize: result.pageSize })` block appears 22+ times across
 * 13 list pages (some pages call setMeta from multiple code paths — initial
 * load, post-mutation refresh, post-create refresh). Centralizing the
 * projection collapses each call site to `setMeta(paginationMetaFromResponse(result))`.
 *
 * Drops the `elements: T[]` field so list-state memory doesn't retain the
 * full last-page array twice (once in `rows`, once in `meta.elements`).
 */
export function paginationMetaFromResponse<T>(
  result: PaginatedResponse<T>,
): PaginationMeta<T> {
  return {
    totalRecords: result.totalRecords,
    totalPage: result.totalPage,
    page: result.page,
    from: result.from,
    pageSize: result.pageSize,
  };
}

/**
 * Single-item or command response wrapper.
 *
 * The Angular `ResponseData<T>` model has `data`, `showMessage`, and `message`.
 * We carry forward the same shape for compatibility but make `showMessage`
 * optional since not all endpoints set it.
 */
export interface ApiResponse<T> {
  data: T;
  message: string;
  showMessage?: boolean;
}
